import { useState, type FC, useMemo, useEffect } from 'react';
import {
  UiDrawer,
  UiDrawerOverlay,
  UiDrawerContent,
  UiDrawerBody,
  UiDrawerCloseButton,
  type UiDrawerProps,
  UiStack,
  UiButton,
  UiHStack,
  UiText,
  uiStyles, UiSpinner,
} from '@/lib/ui';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { cartQueryKey, listOrdersQueryKey, preparePayment, registrationQueryKey, type PaymentIntent, type PreparePaymentRequest } from '@/api/registration';
import { type ApiResponse } from '@/api/tenantClient';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import Cart from '@/registration/component/Register/ShoppingCart/Cart';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useRegisterUpsellSteps } from '@/registration/hook/useRegisterUpsellSteps';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useUserSession } from '@/registration/hook/useUserSession';

export interface PaymentByInvoiceDrawerProps extends Omit<UiDrawerProps, 'children'> {
  isUpsell?: boolean
}

const PaymentByInvoiceDrawer: FC<PaymentByInvoiceDrawerProps> = ({
  isUpsell,
  ...props
}) => {
  const { createTenantApiRequest } = useTenantApi();
  const [errors, setErrors] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { eventId } = useRegisterRoute();
  const registrationQuery = useRegistrationQuery(eventId);
  const { userSession } = useUserSession();
  const { toNextStep: defaultToNextStep } = useRegisterSteps();
  const { toNextStep: upsellToNextStep } = useRegisterUpsellSteps();

  const toNextStep = isUpsell ? upsellToNextStep : defaultToNextStep;

  const cartQuery = useCartQuery(eventId);
  const cartItemErrors = useMemo(() => {
    let cartErrors: string[] = [];
    cartQuery.data?.cartItems.forEach((cartItem) => {
      cartErrors = cartErrors.concat(cartItem.errors ?? []);
    });
    return cartErrors;
  }, [cartQuery.data?.cartItems]);

  const { mutate: preparePaymentMutate, isLoading: isPreparePaymentLoading } = useMutation<ApiResponse<PaymentIntent>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<PaymentIntent>;
    },
    onSuccess: async (order: ApiResponse<PaymentIntent>) => {
      if (order.errors && order.errors.length > 0) {
        setErrors(order.errors);
      } else {
        await queryClient.invalidateQueries({ queryKey: [registrationQuery] });
        await queryClient.invalidateQueries({ queryKey: [listOrdersQueryKey, { eventId, userSession }] });
        await queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
        await queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: registrationQuery?.data?.id }] });

        const orderResponse = order.items?.[0].order;
        if (orderResponse) {
          toNextStep({ additionalParams: { orderHash: orderResponse.secureHash } });
        }
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to save the host.']);
    }
  });

  const onSubmit = () => {
    if (!cartQuery.data) {
      return;
    }
    const preparePaymentData: PreparePaymentRequest = {
      cartId: cartQuery.data.id,
      locale: 'aud',
      gateway: 'invoice'
    };
    preparePaymentMutate(preparePaymentData);
  };

  // Clear error when open Drawer
  useEffect(() => {
    if (!props.isOpen && errors.length > 0) {
      setErrors([]);
    }
  }, [errors.length, props.isOpen]);

  return (
    <UiDrawer placement={'right'} size={'xl'} {...props}>
      <UiDrawerOverlay />
      <UiDrawerContent bgColor={'gray.100'}>
        <UiDrawerCloseButton size={'lg'} fontWeight={'bold'} color={'primary.500'} />
        <QueryContainer
          query={cartQuery}
          loadingComponent={(
            <UiStack p={8}>
              <UiSpinner size={'lg'} color={'primary.500'} thickness='2px' />
            </UiStack>
          )}
        >
          {cart => {
            return (
              <UiDrawerBody px={0} pb={16} alignItems={'stretch'}>
                {errors.length > 0 && (
                  <UiStack spacing={4} flexGrow={1} py={12} px={{ base: 4, lg: 8 }}>
                    {errors.map((error) => {
                      return (
                        <BaseMessageBarError key={error}>
                          {error}
                        </BaseMessageBarError>
                      );
                    })}
                  </UiStack>
                )}
                <UiStack
                  px={{ base: 4, lg: 8 }}
                  py={8}
                  flexGrow={1}
                  spacing={6}
                >
                  <UiText variant={'title'}>Pay by invoice</UiText>
                  <BaseMessageBarInfo
                    borderRadius={uiStyles.borderRadius}
                    bgColor={'#fff'}
                  >
                    We will send you an email with payment details.
                  </BaseMessageBarInfo>
                  <BaseDividerHorizontal height={2} />
                  <Cart showErrors={true} />
                </UiStack>
                <UiHStack px={{ base: 4, lg: 8 }} justifyContent={'flex-end'}>
                  <UiButton
                    px={8}
                    size={'lg'}
                    shadow={'base'}
                    colorScheme={'primary'}
                    onClick={onSubmit}
                    isDisabled={cart.cartItems.length < 1 || cartItemErrors.length > 0}
                    isLoading={isPreparePaymentLoading}>
                  Confirm
                  </UiButton>
                </UiHStack>
              </UiDrawerBody>
            );
          }}
        </QueryContainer>
      </UiDrawerContent>
    </UiDrawer>
  );
};

export default PaymentByInvoiceDrawer;
